import { useState } from "react";

import { startExport } from "../../api/exports";
import type { ExportJob } from "../../api/exports";
import {
  TRANSITION_CATEGORIES,
  VALID_TRANSITION_IDS,
  defaultDurationMs,
} from "../../lib/transitions";
import { ANIMATION_OPTIONS, TEXT_PRESETS } from "../../lib/textPresets";
import { useEditor } from "../../store/editor";
import SubtitlesPanel from "./SubtitlesPanel";

const LUTS = [
  { id: "cinematic.cube", label: "Cinematográfico" },
  { id: "warm.cube", label: "Cálido" },
  { id: "cold.cube", label: "Frío" },
  { id: "vintage.cube", label: "Vintage" },
  { id: "punchy.cube", label: "Vivo (saturado)" },
  { id: "bw.cube", label: "Blanco y negro" },
];

const EFFECT_LABEL: Record<string, string> = {
  adjust: "Ajuste de color",
  lut: "LUT (filtro)",
  blur: "Difuminado",
};

function Slider(props: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; hint?: string;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label className="label" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>{props.label} {props.hint && <span style={{ opacity: 0.6, fontSize: 10 }}>{props.hint}</span>}</span>
        <span style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>
          {props.value.toFixed(2)}
        </span>
      </label>
      <input type="range" min={props.min} max={props.max} step={props.step}
             value={props.value} onChange={(e) => props.onChange(Number(e.target.value))} />
    </div>
  );
}

function AdjustControls(props: { effect: any; onPatch: (p: any) => void }) {
  const e = props.effect;
  return (
    <>
      <Slider label="Exposición" value={e.exposure || 0} min={-2} max={2} step={0.05}
              onChange={(v) => props.onPatch({ exposure: v })} hint="stops" />
      <Slider label="Contraste" value={e.contrast ?? 1} min={0} max={2} step={0.02}
              onChange={(v) => props.onPatch({ contrast: v })} />
      <Slider label="Saturación" value={e.saturation ?? 1} min={0} max={3} step={0.05}
              onChange={(v) => props.onPatch({ saturation: v })} />
      <Slider label="Temperatura" value={e.temperature || 0} min={-1} max={1} step={0.05}
              onChange={(v) => props.onPatch({ temperature: v })} hint="frío ← → cálido" />
    </>
  );
}

function LutControls(props: { effect: any; onPatch: (p: any) => void }) {
  const e = props.effect;
  return (
    <>
      <label className="label">Filtro</label>
      <select value={e.file || ""} onChange={(ev) => props.onPatch({ file: ev.target.value })}>
        {LUTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
      </select>
      <Slider label="Intensidad" value={e.intensity ?? 1} min={0} max={1} step={0.05}
              onChange={(v) => props.onPatch({ intensity: v })} />
    </>
  );
}

function OverlayEditor() {
  const timeline = useEditor((s) => s.timeline);
  const selectedOverlayId = useEditor((s) => s.selectedOverlayId);
  const updateOverlay = useEditor((s) => s.updateOverlay);
  const removeOverlay = useEditor((s) => s.removeOverlay);
  const selectOverlay = useEditor((s) => s.selectOverlay);
  const overlay = (timeline.overlays || []).find((o: any) => o.id === selectedOverlayId) as any;
  if (!overlay) return null;
  return (
    <div style={{ overflowY: "auto", height: "100%", paddingRight: 4 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Texto</h3>
        <button onClick={() => selectOverlay(null)} style={{ fontSize: 11 }}>← Volver</button>
      </div>
      <label className="label" style={{ marginTop: 12 }}>Texto</label>
      <textarea value={overlay.text} rows={3}
                onChange={(e) => updateOverlay(overlay.id, { text: e.target.value })} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
        <div>
          <label className="label">Aparece (s)</label>
          <input type="number" step={0.1} value={(overlay.start / 1000).toFixed(2)}
                 onChange={(e) => updateOverlay(overlay.id, { start: Number(e.target.value) * 1000 })} />
        </div>
        <div>
          <label className="label">Desaparece (s)</label>
          <input type="number" step={0.1} value={(overlay.end / 1000).toFixed(2)}
                 onChange={(e) => updateOverlay(overlay.id, { end: Number(e.target.value) * 1000 })} />
        </div>
      </div>

      <Slider label="Tamaño" value={overlay.font_size || 64} min={16} max={300} step={2}
              onChange={(v) => updateOverlay(overlay.id, { font_size: v })} hint="px (1080p)" />

      <label className="label" style={{ marginTop: 8 }}>Color del texto</label>
      <div style={{ display: "flex", gap: 6 }}>
        <input type="color" value={overlay.color || "#ffffff"}
               onChange={(e) => updateOverlay(overlay.id, { color: e.target.value })}
               style={{ width: 50, height: 32, padding: 0 }} />
        <input value={overlay.color || "#ffffff"}
               onChange={(e) => updateOverlay(overlay.id, { color: e.target.value })} />
      </div>

      <label className="label" style={{ marginTop: 10 }}>Fondo (caja)</label>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <button onClick={() => updateOverlay(overlay.id, { background: null })}
                style={{ fontSize: 11, opacity: overlay.background ? 0.6 : 1 }}>Ninguno</button>
        <button onClick={() => updateOverlay(overlay.id, { background: "rgba(0,0,0,0.55)" })}
                style={{ fontSize: 11 }}>Negro 55%</button>
        <button onClick={() => updateOverlay(overlay.id, { background: "rgba(0,0,0,0.85)" })}
                style={{ fontSize: 11 }}>Negro 85%</button>
        <button onClick={() => updateOverlay(overlay.id, { background: "rgba(255,255,255,0.85)", color: "#000" })}
                style={{ fontSize: 11 }}>Blanco</button>
        <button onClick={() => updateOverlay(overlay.id, { background: overlay.color })}
                style={{ fontSize: 11 }}>= Color</button>
      </div>

      <label className="label" style={{ marginTop: 10 }}>Alineación</label>
      <div style={{ display: "flex", gap: 4 }}>
        {(["left", "center", "right"] as const).map((a) => (
          <button key={a} onClick={() => updateOverlay(overlay.id, { align: a })}
                  style={{ flex: 1, fontSize: 11,
                           background: (overlay.align || "center") === a ? "var(--accent)" : undefined,
                           color: (overlay.align || "center") === a ? "#08222e" : undefined }}>
            {a === "left" ? "⫷ Izq" : a === "center" ? "≡ Centro" : "Der ⫸"}
          </button>
        ))}
      </div>

      <label className="label" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={!!overlay.bold}
               onChange={(e) => updateOverlay(overlay.id, { bold: e.target.checked })} />
        {" "}Negrita
      </label>

      <Slider label="Posición X" value={overlay.x ?? 0.5} min={0} max={1} step={0.01}
              onChange={(v) => updateOverlay(overlay.id, { x: v })} hint="% horizontal" />
      <Slider label="Posición Y" value={overlay.y ?? 0.85} min={0} max={1} step={0.01}
              onChange={(v) => updateOverlay(overlay.id, { y: v })} hint="% vertical" />

      <h4 style={{ marginTop: 16, fontSize: 13 }}>Plantilla rápida</h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {TEXT_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => updateOverlay(overlay.id, p.apply())}
            title={p.desc}
            style={{
              padding: "8px 6px", fontSize: 11,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              minHeight: 56,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700 }}>{p.swatch}</span>
            <span style={{ opacity: 0.85 }}>{p.label}</span>
          </button>
        ))}
      </div>

      <h4 style={{ marginTop: 16, fontSize: 13 }}>Animación</h4>
      <label className="label">Entrada</label>
      <select value={overlay.animation_in || "fade"}
              onChange={(e) => updateOverlay(overlay.id, { animation_in: e.target.value })}>
        {ANIMATION_OPTIONS.map((o) => <option key={o.id} value={o.id || ""}>{o.label}</option>)}
      </select>
      <Slider label="Duración entrada" value={overlay.fade_in_ms || 0} min={0} max={2000} step={50}
              onChange={(v) => updateOverlay(overlay.id, { fade_in_ms: v })} hint="ms" />

      <label className="label" style={{ marginTop: 6 }}>Salida</label>
      <select value={overlay.animation_out || "fade"}
              onChange={(e) => updateOverlay(overlay.id, { animation_out: e.target.value })}>
        {ANIMATION_OPTIONS.map((o) => <option key={o.id} value={o.id || ""}>{o.label}</option>)}
      </select>
      <Slider label="Duración salida" value={overlay.fade_out_ms || 0} min={0} max={2000} step={50}
              onChange={(v) => updateOverlay(overlay.id, { fade_out_ms: v })} hint="ms" />

      <button className="danger" onClick={() => removeOverlay(overlay.id)}
              style={{ width: "100%", marginTop: 16 }}>
        Eliminar este texto
      </button>
    </div>
  );
}

function OverlayList() {
  const timeline = useEditor((s) => s.timeline);
  const addTextOverlay = useEditor((s) => s.addTextOverlay);
  const selectOverlay = useEditor((s) => s.selectOverlay);
  const overlays = (timeline.overlays || []) as any[];
  if (overlays.length === 0) return null;
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                     marginBottom: 6 }}>
        <h4 style={{ margin: 0, fontSize: 13 }}>Textos ({overlays.length})</h4>
        <button onClick={() => addTextOverlay()} style={{ fontSize: 11, padding: "2px 8px" }}>
          + Añadir
        </button>
      </div>
      {overlays.map((o) => (
        <div key={o.id} onClick={() => selectOverlay(o.id)}
             style={{ padding: "6px 8px", marginBottom: 4,
                      background: "var(--bg-elev-2)", borderRadius: 4,
                      fontSize: 11, cursor: "pointer",
                      display: "flex", justifyContent: "space-between" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {o.text || "(vacío)"}
          </span>
          <span style={{ opacity: 0.6 }}>
            {(o.start / 1000).toFixed(1)}–{(o.end / 1000).toFixed(1)}s
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Inspector() {
  const timeline = useEditor((s) => s.timeline);
  const selectedIds = useEditor((s) => s.selectedClipIds);
  const selectedOverlayId = useEditor((s) => s.selectedOverlayId);
  const addTextOverlay = useEditor((s) => s.addTextOverlay);
  const setTimeline = useEditor((s) => s.setTimeline);
  const addClipEffectByType = useEditor((s) => s.addClipEffectByType);
  const updateClipEffect = useEditor((s) => s.updateClipEffect);
  const removeClipEffect = useEditor((s) => s.removeClipEffect);
  const toggleClipEffect = useEditor((s) => s.toggleClipEffect);
  const setClipVolume = useEditor((s) => s.setClipVolume);
  const projectId = useEditor((s) => s.projectId);
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [preset, setPreset] = useState<"720p" | "1080p" | "4k">("1080p");
  const [addOpen, setAddOpen] = useState(false);

  // Si hay overlay seleccionado, prioridad: editar overlay
  if (selectedOverlayId) return <OverlayEditor />;

  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const selected = selectedId ? (() => {
    for (const t of timeline.tracks) {
      const c = t.clips.find((c) => c.id === selectedId);
      if (c) return { clip: c, track: t };
    }
    return null;
  })() : null;

  function patchSelected(patch: any) {
    if (!selectedId) return;
    const tracks = timeline.tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => c.id === selectedId ? { ...c, ...patch } : c),
    }));
    setTimeline({ ...timeline, tracks });
  }

  async function onExport() {
    if (!projectId) return;
    const job = await startExport(projectId, preset, true);
    setExportJob(job);
  }

  if (selectedIds.length > 1) {
    return (
      <div>
        <h3 style={{ marginTop: 0 }}>{selectedIds.length} clips seleccionados</h3>
        <p style={{ color: "var(--text-dim)", fontSize: 12 }}>
          Selecciona un solo clip para ver sus propiedades.
        </p>
        <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 16 }}>
          Acciones disponibles con varios clips:<br/>
          · Mover (arrastrar uno mueve todos)<br/>
          · Borrar (<kbd>Supr</kbd>) · Duplicar (<kbd>Ctrl+D</kbd>)<br/>
          · Copiar / Pegar (<kbd>Ctrl+C</kbd> / <kbd>V</kbd>)
        </p>
        <OverlayList />
      </div>
    );
  }

  if (!selected) {
    return (
      <div style={{ overflowY: "auto", height: "100%" }}>
        <h3 style={{ marginTop: 0 }}>Exportar</h3>
        <label className="label">Calidad</label>
        <select value={preset} onChange={(e) => setPreset(e.target.value as any)}>
          <option value="720p">720p · rápido</option>
          <option value="1080p">1080p Full HD</option>
          <option value="4k">4K Ultra HD</option>
        </select>
        <button className="primary" onClick={onExport} style={{ width: "100%", marginTop: 12 }}>
          Exportar vídeo
        </button>
        {exportJob && (
          <div style={{ marginTop: 16, fontSize: 12 }}>
            Job #{exportJob.id} · {exportJob.status}
            {exportJob.output_url && (
              <a href={exportJob.output_url} download style={{ display: "block", marginTop: 8 }}>
                Descargar resultado
              </a>
            )}
          </div>
        )}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
          <button onClick={() => addTextOverlay()} className="primary"
                  style={{ width: "100%", padding: 10 }}>
            + Añadir texto en pantalla
          </button>
          {projectId && <SubtitlesPanel projectId={projectId} />}
          <OverlayList />
        </div>

        <p style={{ marginTop: 24, color: "var(--text-dim)", fontSize: 12 }}>
          Selecciona un clip en el timeline para editarlo.
        </p>
      </div>
    );
  }

  const clip = selected.clip;
  const track = selected.track;
  const tr = clip.transitionOut;
  const isAudio = track.type === "audio";
  const effects = (clip.effects || []) as any[];

  return (
    <div style={{ overflowY: "auto", height: "100%", paddingRight: 4 }}>
      <h3 style={{ marginTop: 0 }}>Clip seleccionado</h3>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label className="label">Inicio (ms)</label>
          <input type="number" value={clip.in}
                 onChange={(e) => patchSelected({ in: Number(e.target.value) })} />
        </div>
        <div>
          <label className="label">Fin (ms)</label>
          <input type="number" value={clip.out}
                 onChange={(e) => patchSelected({ out: Number(e.target.value) })} />
        </div>
      </div>

      {isAudio && (
        <div style={{ marginTop: 16 }}>
          <Slider
            label="Volumen"
            value={clip.volume ?? 1}
            min={0} max={2} step={0.05}
            onChange={(v) => setClipVolume(clip.id, v)}
            hint={`${Math.round((clip.volume ?? 1) * 100)}%`}
          />
        </div>
      )}

      {/* Stack de efectos (sólo para vídeo/imagen) */}
      {!isAudio && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h4 style={{ margin: 0, fontSize: 13 }}>Efectos ({effects.length})</h4>
          </div>

          {effects.map((e: any) => (
            <div key={e.id} style={{
              marginTop: 10, padding: 10,
              background: "var(--bg-elev-2)",
              border: "1px solid var(--border)", borderRadius: 6,
              opacity: e.enabled === false ? 0.4 : 1,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                            marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{EFFECT_LABEL[e.type] || e.type}</span>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    onClick={() => toggleClipEffect(clip.id, e.id)}
                    style={{ fontSize: 10, padding: "1px 6px" }}
                    title={e.enabled === false ? "Activar" : "Desactivar"}
                  >{e.enabled === false ? "○" : "●"}</button>
                  <button
                    className="danger"
                    onClick={() => removeClipEffect(clip.id, e.id)}
                    style={{ fontSize: 10, padding: "1px 6px" }}
                  >×</button>
                </div>
              </div>
              {e.type === "adjust" && (
                <AdjustControls effect={e}
                  onPatch={(p) => updateClipEffect(clip.id, e.id, p)} />
              )}
              {e.type === "lut" && (
                <LutControls effect={e}
                  onPatch={(p) => updateClipEffect(clip.id, e.id, p)} />
              )}
            </div>
          ))}

          <div style={{ position: "relative", marginTop: 10 }}>
            <button onClick={() => setAddOpen((v) => !v)}
                    style={{ width: "100%", padding: "6px", fontSize: 12 }}>
              + Añadir efecto
            </button>
            {addOpen && (
              <div style={{
                position: "absolute", left: 0, right: 0, top: "100%",
                marginTop: 4, padding: 4, zIndex: 100,
                background: "var(--bg-elev)", border: "1px solid var(--border)",
                borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}>
                {(["adjust", "lut"] as const).map((t) => (
                  <div
                    key={t}
                    onClick={() => { addClipEffectByType(clip.id, t); setAddOpen(false); }}
                    style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderRadius: 4 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-elev-2)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    {EFFECT_LABEL[t]}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transición saliente — 50+ tipos en grupos */}
      <div style={{ marginTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                       marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13 }}>Transición saliente</h4>
          <button
            onClick={() => {
              const all = Array.from(VALID_TRANSITION_IDS);
              const pick = all[Math.floor(Math.random() * all.length)];
              patchSelected({ transitionOut: { type: pick, duration_ms: defaultDurationMs(pick) } });
            }}
            style={{ padding: "1px 8px", fontSize: 10 }}
            title="Probar una transición aleatoria"
          >🎲 Sorpréndeme</button>
        </div>
        <select
          value={tr?.type || ""}
          onChange={(e) => {
            const v = e.target.value;
            patchSelected({
              transitionOut: v
                ? { type: v, duration_ms: tr?.duration_ms || defaultDurationMs(v) }
                : null,
            });
          }}
        >
          <option value="">Sin transición</option>
          {TRANSITION_CATEGORIES.map((cat) => (
            <optgroup key={cat.name} label={cat.name}>
              {cat.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.icon ? `${t.icon}  ${t.label}` : t.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {tr && (
          <>
            <label className="label" style={{ marginTop: 6 }}>Duración (ms)</label>
            <input type="number" value={tr.duration_ms} min={100} max={5000} step={50}
                   onChange={(e) => patchSelected({
                     transitionOut: { ...tr, duration_ms: Number(e.target.value) },
                   })} />
            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
              {[250, 500, 1000, 1500].map((ms) => (
                <button key={ms} onClick={() => patchSelected({
                  transitionOut: { ...tr, duration_ms: ms },
                })} style={{ flex: 1, padding: "2px 4px", fontSize: 10 }}>
                  {ms}ms
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <OverlayList />
    </div>
  );
}
